/**
 * Predictive Memory System Types
 *
 * The AI that anticipates what you need before you realize it.
 *
 * Scoring Algorithm:
 *   - Semantic similarity to current context (40%)
 *   - Temporal relevance (recency decay curve) (30%)
 *   - Usage frequency of similar memories (20%)
 *   - Serendipity factor (adjacent/surprising topics) (10%)
 */

import { z } from "zod";
import {
  ResponseFormatType,
  MemoryTypeValue,
  MemoryQueryContext,
  QueryScope,
} from "./types.js";

// ============================================================================
// Input Parameters
// ============================================================================

/**
 * Context describing the user's current work/focus
 */
export interface PredictiveContext {
  /** Current project or task description */
  currentProject?: string;

  /** Recent work patterns or topics */
  recentTopics?: string[];

  /** Current files being worked on */
  activeFiles?: string[];

  /** Free-form context text (e.g., current task, meeting notes) */
  contextText?: string;

  /** Team channel or conversation topics (for team-aware predictions) */
  teamContext?: string;
}

/**
 * Parameters for predictive memory recall
 */
export interface PredictiveRecallParams extends MemoryQueryContext {
  /** User ID */
  userId: string;

  /** Current context for prediction scoring */
  context: PredictiveContext;

  /** Maximum predictions to return (default: 5, max: 20) */
  limit?: number;

  /** Minimum confidence threshold (0-100, default: 40) */
  minConfidence?: number;

  /** Include serendipity/surprise suggestions (default: true) */
  includeSerendipity?: boolean;

  /** Time window in days for recency scoring (default: 90) */
  timeWindowDays?: number;

  /** Response format */
  responseFormat?: ResponseFormatType;
}

// Zod schema for MCP/API validation
export const PredictiveRecallParamsSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid().optional(),
  topicId: z.string().optional(),
  queryScope: QueryScope.optional(),
  context: z.object({
    currentProject: z.string().optional(),
    recentTopics: z.array(z.string()).optional(),
    activeFiles: z.array(z.string()).optional(),
    contextText: z.string().optional(),
    teamContext: z.string().optional(),
  }),
  limit: z.number().int().min(1).max(20).default(5),
  minConfidence: z.number().min(0).max(100).default(40),
  includeSerendipity: z.boolean().default(true),
  memoryTypes: z.array(z.enum([
    "context", "project", "knowledge", "reference", "personal", "workflow"
  ])).optional(),
  timeWindowDays: z.number().int().min(1).max(365).default(90),
  responseFormat: z.enum(["json", "markdown"]).default("markdown"),
});

// ============================================================================
// Scoring Components
// ============================================================================

/**
 * Individual scoring factors for a prediction
 */
export interface PredictionScoreBreakdown {
  /** Semantic similarity to current context (0-100) */
  semanticScore: number;

  /** Temporal relevance based on age (0-100) */
  temporalScore: number;

  /** Usage frequency of similar content (0-100) */
  frequencyScore: number;

  /** Serendipity/surprise factor (0-100) */
  serendipityScore: number;

  /** Weighted combination (0-100) */
  combinedScore: number;
}

/**
 * Reason why a memory was predicted
 */
export type PredictionReason =
  | "semantic_match"      // High semantic similarity to current context
  | "recent_related"      // Recently viewed similar content
  | "team_trending"       // Team is discussing related topics
  | "pattern_match"       // Matches user's work patterns
  | "adjacent_discovery"  // Serendipitous adjacent topic
  | "expert_topic"        // User has expertise in this area
  | "prerequisite"        // Foundational for current work
  | "frequently_used";    // Often accessed in similar contexts

// ============================================================================
// Output Types
// ============================================================================

/**
 * A single predicted memory with confidence and explanation
 */
export interface PredictedMemory {
  /** Memory ID */
  id: string;

  /** Memory title */
  title: string;

  /** Memory type */
  type: MemoryTypeValue;

  /** Memory tags */
  tags: string[];

  /** Content preview (first 300 chars) */
  contentPreview: string;

  /** Confidence score (0-100) */
  confidence: number;

  /** Human-readable explanation of why this was predicted */
  reason: string;

  /** Structured reason type */
  reasonType: PredictionReason;

  /** Detailed score breakdown */
  scoreBreakdown: PredictionScoreBreakdown;

  /** Days since memory was created */
  daysSinceCreated: number;

  /** Days since memory was last accessed */
  daysSinceAccessed?: number;

  /** Suggested action for the user */
  suggestedAction: "review" | "apply" | "reference" | "explore";

  /** Related topic clusters */
  relatedTopics?: string[];
}

/**
 * Result of predictive recall operation
 */
export interface PredictiveRecallResult {
  /** Predicted memories ranked by confidence */
  predictions: PredictedMemory[];

  /** Total predictions returned */
  totalPredictions: number;

  /** Total memories analyzed */
  memoriesAnalyzed: number;

  /** Context that was used for prediction */
  contextUsed: {
    projectContext: boolean;
    topicsContext: boolean;
    filesContext: boolean;
    textContext: boolean;
    teamContext: boolean;
  };

  /** Scoring weights used */
  scoringWeights: {
    semantic: number;
    temporal: number;
    frequency: number;
    serendipity: number;
  };

  /** Algorithm metadata */
  algorithmInfo: {
    version: string;
    embeddingModel: string;
    timeWindowDays: number;
  };

  /** Timestamp of prediction */
  generatedAt: string;
}

// ============================================================================
// Internal Types (for engine use)
// ============================================================================

/**
 * Internal memory representation with embedding for scoring
 */
export interface ScoredMemoryCandidate {
  id: string;
  title: string;
  content: string;
  type: MemoryTypeValue;
  tags: string[];
  embedding: number[];
  createdAt: Date;
  updatedAt: Date;
  metadata?: Record<string, any>;
  accessCount?: number;
}

/**
 * Scoring configuration (tunable weights)
 */
export interface PredictionScoringConfig {
  /** Weight for semantic similarity (default: 0.40) */
  semanticWeight: number;

  /** Weight for temporal relevance (default: 0.30) */
  temporalWeight: number;

  /** Weight for usage frequency (default: 0.20) */
  frequencyWeight: number;

  /** Weight for serendipity (default: 0.10) */
  serendipityWeight: number;

  /** Decay rate for temporal scoring (half-life in days) */
  temporalDecayHalfLife: number;

  /** Similarity threshold for semantic matching */
  semanticThreshold: number;

  /** Serendipity similarity range (min-max for "adjacent" topics) */
  serendipityRange: {
    min: number;
    max: number;
  };
}

/**
 * Default scoring configuration
 */
export const DEFAULT_SCORING_CONFIG: PredictionScoringConfig = {
  semanticWeight: 0.40,
  temporalWeight: 0.30,
  frequencyWeight: 0.20,
  serendipityWeight: 0.10,
  temporalDecayHalfLife: 14, // 2 weeks
  semanticThreshold: 0.5,
  serendipityRange: {
    min: 0.3,  // Not too similar
    max: 0.6,  // Not too different
  },
};

// ============================================================================
// Feedback Types (for learning loop)
// ============================================================================

/**
 * User feedback on a prediction
 */
export interface PredictionFeedback {
  /** Prediction/memory ID */
  memoryId: string;

  /** User ID */
  userId: string;

  /** Was this prediction useful? */
  useful: boolean;

  /** Action taken */
  action: "clicked" | "saved" | "dismissed" | "ignored";

  /** Optional reason for dismissal */
  dismissReason?: "not_relevant" | "already_know" | "not_now" | "other";

  /** Timestamp */
  timestamp: string;
}

/**
 * Aggregated feedback for model improvement
 */
export interface PredictionAccuracyMetrics {
  /** Total predictions made */
  totalPredictions: number;

  /** Predictions marked as useful */
  usefulPredictions: number;

  /** Predictions clicked/engaged */
  engagedPredictions: number;

  /** Accuracy rate (useful / total) */
  accuracyRate: number;

  /** Engagement rate (engaged / total) */
  engagementRate: number;

  /** Time period analyzed */
  timePeriod: {
    start: string;
    end: string;
  };
}
